/**
 * What an alert is about, by NAME — the store and article the alert carries, named on the alert
 * itself (usability audit B4, DEC-111; bata-platform#508).
 *
 * **Where the names come from.** `AlertSummary.storeName` is the store's name on its market's door
 * master in force; `articleName` is `article_desc` on the market's attribute row in force today —
 * looked up once per page by the platform for the keys the page shows. Each is `null` with its
 * `…AbsentReason` when there is a key and no name (`store_not_in_location_master`,
 * `attributes_not_landed`, `not_published` — the replenishment report's codes), and `null` with a
 * `null` reason when there is no market to name it in (an alert raised before W3-1) or the lookup
 * is not wired: that pair is said as `not_published`, the platform's own "no name and no reason".
 *
 * **Shown as the run's names are** (`features/inseason/run-names.tsx`): the name with its code —
 * «Santafé (1203)» — and, without one, the code in mono whose tooltip says why. Never a guess.
 *
 * Shared because two features show alerts: Management's exception queue and group page, and the
 * in-season workbench.
 */

import { useTranslation } from 'react-i18next';

import type { AlertSummary } from '../api/generated';
import type { ExportCell, ExportColumn } from '../export';

/** The platform's "no name and no reason" — said for a key with neither a name nor a reason. */
export const NOT_PUBLISHED_REASON = 'not_published';

/** One key of an alert: its code, and its name or why there is none. */
export interface AlertNamed {
  readonly code: string;
  readonly name: string | null;
  /** Why `name` is `null`; `null` when named. */
  readonly reason: string | null;
}

type Names = Pick<AlertSummary, 'storeKey' | 'storeName' | 'storeNameAbsentReason' | 'articleKey' | 'articleName' | 'articleNameAbsentReason'>;

function named(code: string | null | undefined, name: string | null | undefined, reason: string | null | undefined): AlertNamed | null {
  const key = code?.trim() ?? '';
  if (key === '') return null;
  const published = name?.trim() ?? '';
  if (published !== '') return { code: key, name: published, reason: null };
  return { code: key, name: null, reason: reason ?? NOT_PUBLISHED_REASON };
}

/** The alert's store, named; `null` when the alert has no store (a cycle alert). Pure. */
export function alertStore(alert: Names): AlertNamed | null {
  return named(alert.storeKey, alert.storeName, alert.storeNameAbsentReason);
}

/** The alert's article, named; `null` when the alert has no article. Pure. */
export function alertArticle(alert: Names): AlertNamed | null {
  return named(alert.articleKey, alert.articleName, alert.articleNameAbsentReason);
}

const REASON_KEY: Readonly<Record<string, string>> = {
  store_not_in_location_master: 'alertNames.absent.storeNotInLocationMaster',
  attributes_not_landed: 'alertNames.absent.attributesNotLanded',
  not_published: 'alertNames.absent.notPublished',
};

/**
 * The words for why a name is absent. The alert's article name is read TODAY, not on a run's date,
 * so these are the alert's own sentences; a code this build does not know says so.
 */
export function alertNameAbsentKey(reason: string | null): string {
  return (reason === null ? undefined : REASON_KEY[reason]) ?? 'alertNames.absent.unrecognised';
}

type Translate = (key: string, values: Record<string, string>) => string;

/** «Santafé (1203)» where named, the code alone otherwise. */
export function alertNamedText(t: Translate, one: AlertNamed): string {
  return one.name === null ? one.code : t('alertNames.withCode', { name: one.name, code: one.code });
}

/**
 * The subject as a sentence: the store and the article by name, «·» between them — else the
 * platform's own `subjectId` (a cycle alert names a cycle and a market, no store). Pure.
 */
export function alertSubjectText(t: Translate, alert: Names & Pick<AlertSummary, 'subjectId'>): string {
  const parts = [alertStore(alert), alertArticle(alert)].filter((one): one is AlertNamed => one !== null);
  return parts.length === 0 ? alert.subjectId : parts.map((one) => alertNamedText(t, one)).join(' · ');
}

/** One key in a sentence or cell: «Santafé (1203)», or the code in mono whose tooltip says why. */
export function AlertNameText({ named: one }: { readonly named: AlertNamed }): React.JSX.Element {
  const { t } = useTranslation();
  if (one.name !== null) return <span data-name-of={one.code}>{alertNamedText(t, one)}</span>;
  const why = t(alertNameAbsentKey(one.reason));
  return (
    <span className="font-numeric" title={why} data-name-absent={one.reason ?? NOT_PUBLISHED_REASON}>
      {one.code}
      <span className="sr-only">{` — ${why}`}</span>
    </span>
  );
}

/**
 * «Soggetto»: the store and the article by name, one per line; the platform's `subjectId` (in its
 * tooltip too) when the alert carries neither.
 */
export function AlertSubject({ alert }: { readonly alert: Names & Pick<AlertSummary, 'subjectId'> }): React.JSX.Element {
  const parts = [alertStore(alert), alertArticle(alert)].filter((one): one is AlertNamed => one !== null);
  if (parts.length === 0) return <span className="font-numeric">{alert.subjectId}</span>;
  return (
    <span className="flex flex-col" title={alert.subjectId} data-alert-subject-id={alert.subjectId}>
      {parts.map((one) => <AlertNameText key={one.code} named={one} />)}
    </span>
  );
}

/** A name's cell beside its code column: the name, or «No name» in words (ADR 9061) whose tooltip says why. */
export function AlertNameCell({ named: one, className }: { readonly named: AlertNamed | null; readonly className: string }): React.JSX.Element {
  const { t } = useTranslation();
  if (one === null) return <td className={`${className} text-ink-muted`}>{t('inseason.names.absent.label')}</td>;
  if (one.name !== null) return <td className={className} data-name-of={one.code}>{one.name}</td>;
  const why = t(alertNameAbsentKey(one.reason));
  return (
    <td className={`${className} text-ink-muted`} title={why} data-name-absent={one.reason ?? NOT_PUBLISHED_REASON}>
      <span aria-hidden>{t('inseason.names.absent.label')}</span>
      <span className="sr-only">{why}</span>
    </td>
  );
}

// ── the same names in a file ─────────────────────────────────────────────────

/**
 * The alert files' store and article columns: each code, each name, then each name's absent reason
 * (technical — a key ending `_absent_reason` goes to «Tecnico»).
 */
export const ALERT_NAME_EXPORT_COLUMNS: readonly ExportColumn[] = [
  {
    key: 'store_key', headerKey: 'alertNames.column.storeKey', kind: 'text',
    descriptionKey: 'alertNames.columnGuide.storeKey', absent: { notRanged: 'blank' },
  },
  {
    key: 'store_name', headerKey: 'alertNames.column.storeName', kind: 'text',
    descriptionKey: 'alertNames.columnGuide.storeName', absent: { notRanged: 'blank' },
  },
  {
    key: 'article_key', headerKey: 'alertNames.column.articleKey', kind: 'text',
    descriptionKey: 'alertNames.columnGuide.articleKey', absent: { notRanged: 'blank' },
  },
  {
    key: 'article_name', headerKey: 'alertNames.column.articleName', kind: 'text',
    descriptionKey: 'alertNames.columnGuide.articleName', absent: { notRanged: 'blank' },
  },
  {
    key: 'store_name_absent_reason', headerKey: 'alertNames.column.absentReason', headerValues: { column: 'store_name' },
    kind: 'text', descriptionKey: 'alertNames.columnGuide.nameAbsentReason',
  },
  {
    key: 'article_name_absent_reason', headerKey: 'alertNames.column.absentReason', headerValues: { column: 'article_name' },
    kind: 'text', descriptionKey: 'alertNames.columnGuide.nameAbsentReason',
  },
];

/** The keys of `ALERT_NAME_EXPORT_COLUMNS`, in order. */
export const ALERT_NAME_EXPORT_KEYS: readonly string[] = ALERT_NAME_EXPORT_COLUMNS.map((column) => column.key);

const NOT_APPLICABLE: ExportCell = { kind: 'missing', state: 'not-ranged' };

/** One alert's cells, in `ALERT_NAME_EXPORT_COLUMNS`' order. A key the alert lacks does not apply. Pure. */
export function alertNameExportCells(alert: Names): ExportCell[] {
  const store = alertStore(alert);
  const article = alertArticle(alert);
  const code = (one: AlertNamed | null): ExportCell => (one === null ? NOT_APPLICABLE : { kind: 'text', value: one.code });
  const name = (one: AlertNamed | null): ExportCell => (one === null
    ? NOT_APPLICABLE
    : one.name === null ? { kind: 'missing', state: 'not-reported' } : { kind: 'text', value: one.name });
  const reason = (one: AlertNamed | null): ExportCell => ({ kind: 'text', value: one === null || one.name !== null ? '' : one.reason ?? NOT_PUBLISHED_REASON });
  return [code(store), name(store), code(article), name(article), reason(store), reason(article)];
}
